import React from 'react';
import {
  TrendingUp,
  MessageSquare,
  Send,
  CheckCheck,
  Eye,
  XCircle,
  Inbox,
  Megaphone,
  UserMinus,
} from 'lucide-react';
import { useGetWhatsappMetricsQuery } from '@/client/api/whatsapp/stats.query';
import { useListFilters } from '@/client/hooks/useListFilters';
import { WhatsAppStats } from '@/client/types/whatsapp';

interface MetricCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, color }) => (
  <div className="bg-white p-5 rounded-lg border border-gray-200">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-gray-600">{title}</span>
      <div className={`w-9 h-9 ${color} rounded-lg flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900 mb-1">{value.toLocaleString()}</p>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
  </div>
);

// Missing / non-numeric values render as 0 rather than blank or NaN.
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

const RANGES: { key: string; label: string; days: number }[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
];

const AnalyticsPage: React.FC = () => {
  // The selected range persists in the URL (?range=) across reloads.
  const { filters, handleFilterChange } = useListFilters<{ range: string }>({
    initialFilters: { range: '30d' },
  });
  const dateRange = filters.range;
  const setDateRange = (v: string) => handleFilterChange('range', v);
  const periodDays = RANGES.find(r => r.key === dateRange)?.days ?? 30;

  const { data, isLoading, isError } = useGetWhatsappMetricsQuery({ period_days: periodDays });

  // The proxy may return the stats object directly or wrapped in `{ status, data }`.
  const stats = ((data as any)?.data ?? data) as WhatsAppStats | undefined;

  const outbound = {
    sent: num(stats?.outbound?.sent),
    delivered: num(stats?.outbound?.delivered),
    read: num(stats?.outbound?.read),
    failed: num(stats?.outbound?.failed),
    delivery_rate: num(stats?.outbound?.delivery_rate),
  };
  const inbound = {
    total_all_time: num(stats?.inbound?.total_all_time),
    last_n_days: num(stats?.inbound?.last_n_days),
  };
  const campaigns = {
    total: num(stats?.campaigns?.total),
    running: num(stats?.campaigns?.running),
    completed: num(stats?.campaigns?.completed),
    failed: num(stats?.campaigns?.failed),
  };
  const optouts = { total: num(stats?.optouts?.total) };

  const pct = (part: number, whole: number) =>
    whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;

  const funnel = [
    { label: 'Sent', value: outbound.sent, pct: 100, color: 'bg-blue-500' },
    { label: 'Delivered', value: outbound.delivered, pct: pct(outbound.delivered, outbound.sent), color: 'bg-green-500' },
    { label: 'Read', value: outbound.read, pct: pct(outbound.read, outbound.sent), color: 'bg-purple-500' },
    { label: 'Failed', value: outbound.failed, pct: pct(outbound.failed, outbound.sent), color: 'bg-red-500' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Monitor your WhatsApp messaging performance</p>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map(range => (
              <button
                key={range.key}
                onClick={() => setDateRange(range.key)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  dateRange === range.key
                    ? 'bg-green-500 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            Couldn't load analytics. Showing zeros for all metrics.
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Messages Sent"
            value={outbound.sent}
            subtitle={isLoading ? 'Loading…' : `Last ${periodDays} days`}
            icon={<Send className="w-5 h-5 text-blue-600" />}
            color="bg-blue-100"
          />
          <MetricCard
            title="Delivered"
            value={outbound.delivered}
            subtitle={`${outbound.delivery_rate}% delivery rate`}
            icon={<CheckCheck className="w-5 h-5 text-green-600" />}
            color="bg-green-100"
          />
          <MetricCard
            title="Read"
            value={outbound.read}
            subtitle={`${pct(outbound.read, outbound.sent)}% of sent`}
            icon={<Eye className="w-5 h-5 text-purple-600" />}
            color="bg-purple-100"
          />
          <MetricCard
            title="Failed"
            value={outbound.failed}
            subtitle={`${pct(outbound.failed, outbound.sent)}% of sent`}
            icon={<XCircle className="w-5 h-5 text-red-600" />}
            color="bg-red-100"
          />
        </div>

        {/* Funnel + Inbound */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Delivery Funnel */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Outbound Delivery Funnel
            </h2>
            <div className="space-y-4">
              {funnel.map(item => (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">{item.label}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {item.value.toLocaleString()} ({item.pct}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inbound + Opt-outs */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Inbox className="w-5 h-5" />
              Inbound
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-400" /> Last {periodDays} days
                </span>
                <span className="text-lg font-bold text-gray-900">{inbound.last_n_days.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <Inbox className="w-4 h-4 text-gray-400" /> All time
                </span>
                <span className="text-lg font-bold text-gray-900">{inbound.total_all_time.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <UserMinus className="w-4 h-4 text-gray-400" /> Opt-outs
                </span>
                <span className="text-lg font-bold text-gray-900">{optouts.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Campaigns
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total', value: campaigns.total, color: 'text-gray-900' },
              { label: 'Running', value: campaigns.running, color: 'text-blue-600' },
              { label: 'Completed', value: campaigns.completed, color: 'text-green-600' },
              { label: 'Failed', value: campaigns.failed, color: 'text-red-600' },
            ].map(c => (
              <div key={c.label} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className={`text-2xl font-bold ${c.color}`}>{c.value.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
